// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { nativeVerticalScrollStyles } from 'components/common/scroll/nativeScrollStyles';
import { StrategyType, UseCase, CameraCalibration } from '@tls/sw91-communication/types/com.base';
import ProjectSettingsForm from 'components/project/settings/ProjectSettingsForm';
import { ProjectSettings } from 'components/projectSelection/common/ProjectSettingTypes';
import { ToastByStatus } from 'components/treactui-template/organisms/toast/ToastMessage';
import { DEFAULT_CAMERA_CALIBRATON } from 'hooks/useCameraCalibration';
import useProjectTrainingApi from 'hooks/useProjectTrainingApi';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { toggleCreateDialog } from 'store/reducers/projectSettingsSlice';
import { fetchProjects } from 'store/reducers/projectsSlice';
import { Dialog, FooterButton } from '@tls/treact-ui';
import { IconLoading } from '@tls/treact-ui/icons';
import useAppPermissions from 'hooks/useAppPermissions';
import styled from 'styled-components';
import ProjectSettingsWarnings from './ProjectSettingsWarnings';
import { canSaveProject, getProjectChangeRequirements, getSettingsForRequest } from './createProjectDialogHelpers';
import useSubmitProject from './useSubmitProject';

enum Page {
  componentDetection,
  qualityControl,
}

type Props = {
  page: Page;
  sourceUseCase?: UseCase;
  sourceStrategy?: StrategyType;
  sourceForegroundLabelCount?: number;
  saveOnEnter?: boolean;
};

export default function CreateProjectDialogContent({
  page,
  sourceUseCase,
  sourceStrategy,
  sourceForegroundLabelCount,
  saveOnEnter = false,
}: Readonly<Props>) {
  const { id, duplicateSourceId } = useAppSelector(s => s.projectSettings);
  const activeUseCase = page === Page.componentDetection ? UseCase.COMPONENT_DETECTION : UseCase.QUALITY_CHECK;

  const { t } = useTranslation();
  const { hasMultiLabelCDPermision } = useAppPermissions();

  const [newSettings, setNewSettings] = useState<ProjectSettings>({
    labelsInfo: [],
    projectName: '',
    tags: [],
    strategy: StrategyType.SIMPLE_STRATEGY,
    useCase: UseCase.COMPONENT_DETECTION,
    scaleFactorX: 1000,
    scaleFactorY: 1000,
  });
  const [cameraCalibration, setCameraCalibration] = useState<CameraCalibration>(DEFAULT_CAMERA_CALIBRATON);
  const [confirmedResetKey, setConfirmedResetKey] = useState<string>();
  const [confirmedImageLabelResetKey, setConfirmedImageLabelResetKey] = useState<string>();
  const [outputFooterHeight, setOutputFooterHeight] = useState(0);

  const onChange = useCallback((newSettings: ProjectSettings, cameraCalibration: CameraCalibration) => {
    setNewSettings(newSettings);
    setCameraCalibration(cameraCalibration);
  }, []);

  const dispatch = useAppDispatch();

  const onClose = useCallback(() => {
    dispatch(toggleCreateDialog());
  }, [dispatch]);

  const headerLabel = duplicateSourceId ? 'project.settings.duplicate.title' : 'project.settings.edit.title';

  const containerRef = useRef<HTMLDivElement>(null);
  const outputFooterRef = useRef<HTMLDivElement>(null);

  const { loading, submitProject } = useSubmitProject(id, duplicateSourceId);
  const {
    hasModel,
    loading: loadingHasModel,
    status: trainingHistoryStatus,
  } = useProjectTrainingApi(id ?? '', {
    enabled: Boolean(id),
    showErrorToast: false,
  });

  const modelCheckFailed = trainingHistoryStatus === 'error';
  const {
    sourceSettingsLoaded,
    currentForegroundLabelCount,
    requiresTrainingDataReset,
    modelCheckPending,
    requiresResetConfirmation,
    requiresImageLabelResetConfirmation,
    hasMultiLabelLicenseError,
  } = getProjectChangeRequirements({
    id,
    sourceUseCase,
    sourceStrategy,
    sourceForegroundLabelCount,
    activeUseCase,
    settings: newSettings,
    loadingHasModel,
    modelCheckFailed,
    hasModel,
    hasMultiLabelCDPermision,
  });
  const resetKey = `${String(activeUseCase)}:${String(newSettings.strategy)}:${String(currentForegroundLabelCount)}`;
  const imageLabelResetKey = `${String(sourceForegroundLabelCount)}:${String(currentForegroundLabelCount)}`;
  const resetConfirmed = confirmedResetKey === resetKey;
  const imageLabelResetConfirmed = confirmedImageLabelResetKey === imageLabelResetKey;
  const canSave = canSaveProject({
    settings: newSettings,
    currentForegroundLabelCount,
    loading,
    sourceSettingsLoaded,
    modelCheckPending,
    hasMultiLabelLicenseError,
    requiresResetConfirmation,
    resetConfirmed,
    requiresImageLabelResetConfirmation,
    imageLabelResetConfirmed,
  });

  useEffect(() => {
    setConfirmedResetKey(undefined);
  }, [requiresTrainingDataReset, resetKey]);

  useEffect(() => {
    setConfirmedImageLabelResetKey(undefined);
  }, [imageLabelResetKey, requiresImageLabelResetConfirmation]);

  const showWarnings = !hasMultiLabelLicenseError && (requiresResetConfirmation || requiresImageLabelResetConfirmation);
  const showOutputFooter = hasMultiLabelLicenseError || showWarnings;

  useEffect(() => {
    const outputFooter = outputFooterRef.current;
    if (!showOutputFooter || !outputFooter) {
      setOutputFooterHeight(0);
      return;
    }

    const updateOutputFooterHeight = () => {
      setOutputFooterHeight(Math.ceil(outputFooter.getBoundingClientRect().height));
    };
    updateOutputFooterHeight();

    const resizeObserver = new ResizeObserver(updateOutputFooterHeight);
    resizeObserver.observe(outputFooter);
    return () => resizeObserver.disconnect();
  }, [showOutputFooter]);

  const onSave = useCallback(async () => {
    if (!canSave) return;

    const activeSettings = { ...newSettings, useCase: activeUseCase };
    const requestSettings = duplicateSourceId
      ? { settings: activeSettings, cameraCalibration }
      : getSettingsForRequest(activeSettings, cameraCalibration);
    const { projectName } = requestSettings.settings;
    const { status, errorKey, successKey } = await submitProject(requestSettings);

    dispatch(fetchProjects());

    ToastByStatus(status, t(headerLabel), t(errorKey, { name: projectName }), t(successKey, { name: projectName }));

    if (status < 200 || status >= 400) return;
    dispatch(toggleCreateDialog());
  }, [
    activeUseCase,
    cameraCalibration,
    canSave,
    dispatch,
    duplicateSourceId,
    headerLabel,
    newSettings,
    submitProject,
    t,
  ]);

  const handleEnterPress = useCallback(
    (event: KeyboardEvent) => {
      const { key } = event;

      if (key === 'Enter') {
        onSave();
      }
    },
    [onSave]
  );

  useEffect(() => {
    if (!saveOnEnter) return;
    window.addEventListener('keydown', handleEnterPress);

    return () => {
      window.removeEventListener('keydown', handleEnterPress);
    };
  }, [saveOnEnter, handleEnterPress]);

  const saveIcon = loading || modelCheckPending ? <IconLoading /> : undefined;

  return (
    <>
      <ProjectSettingsDialogBody>
        <Dialog.DialogContent>
          <ProjectSettingsDialogScroll ref={containerRef} $outputFooterHeight={outputFooterHeight}>
            {page === Page.componentDetection || page === Page.qualityControl ? (
              <ProjectSettingsForm onChange={onChange} containerRef={containerRef} useCase={activeUseCase} />
            ) : null}
          </ProjectSettingsDialogScroll>
        </Dialog.DialogContent>
      </ProjectSettingsDialogBody>
      <ProjectSettingsWarnings
        show={showOutputFooter}
        outputFooterRef={outputFooterRef}
        hasMultiLabelLicenseError={hasMultiLabelLicenseError}
        requiresResetConfirmation={requiresResetConfirmation}
        modelCheckFailed={modelCheckFailed}
        resetConfirmed={resetConfirmed}
        onResetConfirmationChange={() => setConfirmedResetKey(resetConfirmed ? undefined : resetKey)}
        requiresImageLabelResetConfirmation={requiresImageLabelResetConfirmation}
        imageLabelResetConfirmed={imageLabelResetConfirmed}
        onImageLabelResetConfirmationChange={() =>
          setConfirmedImageLabelResetKey(imageLabelResetConfirmed ? undefined : imageLabelResetKey)
        }
      />
      <Dialog.Footer
        right={[
          <FooterButton
            key='cancel'
            label={t('cancel')}
            onClick={onClose}
            variant='secondary'
            data-testid='project-settings-dialog-cancel-button'
          />,
          <FooterButton
            icon={saveIcon}
            key='save'
            label={t('save')}
            disabled={!canSave}
            onClick={onSave}
            data-testid='project-settings-dialog-save-button'
          />,
        ]}
      />
    </>
  );
}

const ProjectSettingsDialogBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const ProjectSettingsDialogScroll = styled.div<{ $outputFooterHeight: number }>`
  flex: 1;
  overflow-x: hidden;
  ${nativeVerticalScrollStyles};
  padding-bottom: ${({ $outputFooterHeight }) => `${$outputFooterHeight}px`};
`;
